import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from './utils';
import type {
  ProgramCreateRequest,
  ProgramListItem,
  ProgramUpdateRequest,
  PlanningUnitUpdateRequest,
} from '@/generated/types/navigator/types.gen';

export const programApi = createApi({
  reducerPath: 'programApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['programs', 'plan', 'stats'],
  endpoints: (builder) => ({
    getAllProgram: builder.query<
      Record<number, ProgramListItem>,
      { year?: string }
    >({
      query: ({ year } = {}) => ({
        url: '/v1/program/',
        method: 'GET',
        params: year ? { year } : undefined,
      }),
      transformResponse: (response: ProgramListItem[]) =>
        Object.fromEntries(response.map((p) => [p.id, p])),
      providesTags: ['programs'],
    }),
    getProgramById: builder.query<
      ProgramListItem,
      { programId: number; year?: string }
    >({
      query: ({ programId, year }) => ({
        url: `/v1/program/${programId}`,
        method: 'GET',
        params: year ? { year } : undefined,
      }),
      providesTags: (_result, _error, { programId }) => [
        { type: 'programs', id: programId },
      ],
    }),
    createProgram: builder.mutation<ProgramListItem, ProgramCreateRequest>({
      query: (body) => ({
        url: '/v1/program/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['programs'],
    }),
    updateProgramById: builder.mutation<
      { success: boolean; message: string },
      { programId: number; body: ProgramUpdateRequest }
    >({
      query: ({ programId, body }) => ({
        url: `/v1/program/${programId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['programs', 'stats'],
    }),
    deleteProgramById: builder.mutation<void, { programId: number }>({
      query: ({ programId }) => ({
        url: `/v1/program/${programId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['programs', 'plan'],
    }),
    updatePlanningUnit: builder.mutation<
      PlanningUnitUpdateRequest,
      {
        programId: number;
        referenceName: string;
        body: PlanningUnitUpdateRequest;
      }
    >({
      query: ({ programId, referenceName, body }) => ({
        url: `/v1/program/${programId}/units/${encodeURIComponent(referenceName)}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['programs', 'plan'],
    }),
  }),
});

export const resetProgramApiState = programApi.util.resetApiState;

export const {
  useGetAllProgramQuery,
  useGetProgramByIdQuery,
  useCreateProgramMutation,
  useUpdateProgramByIdMutation,
  useDeleteProgramByIdMutation,
  useUpdatePlanningUnitMutation,
} = programApi;
